import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Checkbox, IconButton,
    List, ListItem, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import service from 'admin/api/services';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

const STORAGE_KEY = "fcm_tokens";

export default function FCMTokenDialog({ open, onClose, title = '', message = '' }) {
    const [token, setToken] = useState('');
    const [tokens, setTokens] = useState([]);
    const [selectedTokens, setSelectedTokens] = useState([]);
    const navigate = useNavigate();

    // Load tokens from localStorage on component mount
    useEffect(() => {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        setTokens(saved);
    }, []);

    // Save tokens to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    }, [tokens]);

    const handleAdd = () => {
        const trimmed = token.trim();
        if (trimmed && !tokens.includes(trimmed)) {
            const newTokens = [...tokens, trimmed];
            setTokens(newTokens);
            setToken('');
        }
    };

    const handleDelete = (index) => {
        const newTokens = [...tokens];
        const [removed] = newTokens.splice(index, 1);
        setTokens(newTokens);
        setSelectedTokens((prev) => prev.filter(t => t !== removed));
    };

    const handleSelect = (tokenValue) => {
        setSelectedTokens(prev =>
            prev.includes(tokenValue)
                ? prev.filter(t => t !== tokenValue)
                : [...prev, tokenValue]
        );
    };

    const sendToTest = async () => {
        try {
            const payload = { 
                tokens: selectedTokens,
                title: title || 'Test Notification',
                message: message || 'This is a test notification'
            };
            const response = await service.notification.test(payload);
            if ([200, 201].includes(response.status)) {
                toast.success(response?.data?.message || 'Test message sent successfully.');
                onClose();
                navigate(-1);
            } else {
                toast.error(response?.data?.message || 'Failed to send test message.');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Something went wrong.');
            console.error('Submission error:', error);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <SoftTypography variant="h5" sx={{
                marginTop: '14px',
                marginLeft: '15px'
            }}>Test on Device</SoftTypography>
            <DialogContent>
                <p style={{ fontSize: '15px' }}>
                    You can test this campaign by entering or selecting the FCM registration tokens of your development device below.
                </p>

                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <SoftInput
                        fullWidth
                        size="small"
                        placeholder="Enter FCM registration token"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                    />
                    <Button variant="contained" onClick={handleAdd}>Add</Button>
                </div>

                <List>
                    {tokens.map((item, index) => (
                        <ListItem key={index} disablePadding>
                            <Checkbox
                                checked={selectedTokens.includes(item)}
                                onChange={() => handleSelect(item)}
                            />
                            <ListItemText
                                primary={item}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '14px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                    }
                                }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton edge="end" onClick={() => handleDelete(index)}>
                                    <DeleteIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                {/* <Button variant="outlined" onClick={onClose}>Cancel</Button> */}
                <SoftButton
                    variant="outlined"
                    color="info"
                    onClick={onClose}
                >
                    Cancel
                </SoftButton>
                <SoftButton
                    color="info" variant="gradient"
                    onClick={sendToTest}
                    disabled={selectedTokens.length === 0}
                >
                    Send Test Message
                </SoftButton>
            </DialogActions>
        </Dialog >
    );
}
